// ===========================================================================
// Driving somebody else's player.
//
// Every provider that can be steered gets one adapter behind a single
// interface, so the room UI never learns a provider's name:
//
//   createVideoAdapter(control, frame, handlers) -> {
//     attemptsSync,                  // false when we will not even try
//     send(verb, arg),               // 'play' | 'pause' | 'seek' (ms)
//     destroy(),
//   }
//
//   handlers: {
//     onReady, onPosition(ms), onPlaying(bool), onError(code, msg),
//     onDuration(ms),   // so the host can be given a timeline
//     onVideoId(id),    // the player moved itself to a different video
//     onEnded(),
//   }
//
// ATTEMPTING is not the same as WORKING, and the difference is the important
// part of this file. These protocols are semi-documented at best; several of
// the ones below are written from a published description I have not been able
// to exercise against real content. So nothing here promises anything: the
// room only shows shared controls once a player has actually ANSWERED, and a
// provider whose player never replies degrades to "everyone plays it
// themselves" on its own. A wrong guess costs a silent handshake, not a
// timeline that does nothing when you drag it.
//
// Confidence, stated plainly:
//   * youtube, vimeo -- exercised against real videos, in a browser.
//   * soundcloud, rutube, ok, niconico, dailymotion, vk -- written from each
//     platform's published protocol, NOT yet confirmed end to end.
//
// NO third-party script runs on the page, for any provider. Doing this
// properly would mean loading each site's player SDK and widening script-src
// to their origins. postMessage is more fragile, but a fragile integration is
// recoverable and third-party script on an origin that also serves minors is
// not.
//
// Two things every adapter must get right, both learned the hard way:
//
//   * Do not post until the frame has LOADED. Until an iframe navigates, its
//     contentWindow is the initial about:blank with an opaque origin, and a
//     targeted postMessage to it is discarded by the browser.
//   * Accept a reply on EITHER identity -- the window we framed, or the origin
//     we framed it from. Origin alone drops everything a frame with an opaque
//     origin says; source alone drops players that post from a frame nested
//     inside their own. Each mistake looks identical from outside: a player
//     that appears to be mute, and a room that offers no shared controls.
//
// ASCII only.
// ===========================================================================

const WIDGET_ID = 'lumi-room-video';
// Enough attempts to outlast a slow player, few enough to stop bothering one
// that is never going to answer. Roughly nine seconds of trying.
const HANDSHAKE_TRIES = 12;
const HANDSHAKE_EVERY_MS = 750;

/**
 * Shared plumbing: load-gated posting, a retrying handshake, teardown.
 *
 * `ready` means "this player has said something to us", which is NOT the same
 * as "our handshake landed" -- see start(). Keeping the two apart matters:
 * conflating them silences every player that greets you first.
 */
function base(frame, targetOrigin, { onMessage, handshake }) {
  let loaded = false;
  let ready = false;
  let dead = false;
  let timer = null;

  const post = (msg) => {
    if (dead || !loaded || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(typeof msg === 'string' ? msg : JSON.stringify(msg), targetOrigin);
    } catch {
      /* mid-reload; the next sync will try again */
    }
  };

  const listener = (e) => {
    if (dead || !frame) return;
    // Accept EITHER identity: the window we framed, or the origin we framed it
    // from. Neither alone is enough.
    //
    // Origin alone fails when a frame reports an opaque origin ('null'), which
    // silently threw away everything YouTube said. Source alone fails when a
    // player posts from a frame nested inside ours -- OK.ru announces itself
    // with {"event":"inited"} from somewhere below its own top document, and an
    // identity check drops it, so the room decided the player was mute and
    // offered no shared controls at all.
    //
    // Together they are still tight: a message must come from the frame we
    // built or from the exact origin we chose to embed, and there is only ever
    // one video frame on the page.
    if (e.source !== frame.contentWindow && e.origin !== targetOrigin) return;
    let data = e.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!data || typeof data !== 'object') return;
    onMessage(data, () => {
      ready = true;
    });
  };
  window.addEventListener('message', listener);

  const start = () => {
    loaded = true;
    if (!handshake) return;
    // Send the handshake REPEATEDLY, on a fixed schedule, and do not let the
    // player's own chatter stop it.
    //
    // Two failures live here, and they are opposites, which is why I walked
    // from one straight into the other:
    //
    //   * Stopping when `ready` is set. `ready` means ANY message arrived, and
    //     SoundCloud, RuTube and Niconico all greet you unprompted -- so their
    //     hello cancelled the handshake and the addEventListener subscriptions
    //     that make them report anything were never sent.
    //   * Sending once at the load event. A widget is not listening the
    //     instant its document loads; it attaches its own handler a moment
    //     later, so a single early send is simply dropped.
    //
    // Both look identical from outside: a player that greets you and then goes
    // quiet forever. Repeating on a timer covers both without having to know
    // when any particular player becomes ready, and re-subscribing is harmless
    // -- these APIs all take addEventListener more than once.
    let asks = 0;
    const ask = () => {
      asks += 1;
      handshake(post);
      if (dead || asks >= HANDSHAKE_TRIES) {
        clearInterval(timer);
        timer = null;
      }
    };
    ask(); // once now, in case it is already listening
    timer = setInterval(ask, HANDSHAKE_EVERY_MS);
  };
  frame.addEventListener('load', start, { once: true });

  return {
    post,
    isReady: () => ready,
    destroy() {
      dead = true;
      if (timer) clearInterval(timer);
      window.removeEventListener('message', listener);
      frame.removeEventListener('load', start);
    },
  };
}

/** Build the returned shape, so every adapter agrees on it. */
function adapter(core, send) {
  return {
    attemptsSync: true,
    send(verb, arg) {
      if (!core.isReady()) return;
      send(verb, Math.max(0, Number(arg) || 0));
    },
    destroy: core.destroy,
  };
}

// ---- YouTube ---------------------------------------------------------------
// Confirmed working. id and channel are required by the protocol, not
// decoration -- messages without them are ignored silently.
function youtubeAdapter(frame, h) {
  const ERRORS = {
    2: 'YouTube rejected that link.',
    5: 'This video cannot be played in this browser.',
    100: 'That video does not exist any more.',
    101: 'The owner of that video does not allow it to play outside YouTube.',
    150: 'The owner of that video does not allow it to play outside YouTube.',
  };

  const core = base(frame, 'https://www.youtube-nocookie.com', {
    handshake: (post) => post({ event: 'listening', id: WIDGET_ID, channel: 'widget' }),
    onMessage(data, markReady) {
      markReady();
      h.onReady?.();

      const raw = data.event === 'onError' ? (data.info ?? data.data) : data.info?.errorCode;
      const code = Number(typeof raw === 'object' && raw ? raw.errorCode : raw);
      if (Number.isFinite(code) && code > 0) {
        h.onError?.(code, ERRORS[code] || 'YouTube could not play this video.');
        return;
      }

      const info = data.info || {};
      if (Number.isFinite(info.currentTime)) h.onPosition?.(info.currentTime * 1000);
      if (Number.isFinite(info.duration) && info.duration > 0) h.onDuration?.(info.duration * 1000);
      // Which video the PLAYER thinks it is on. Not always the one we asked
      // for: clicking a suggestion at the end navigates the embed without
      // telling anybody, and the room silently comes apart.
      const loaded = info.videoData && info.videoData.video_id;
      if (typeof loaded === 'string' && loaded) h.onVideoId?.(loaded);
      if (typeof info.playerState === 'number') {
        h.onPlaying?.(info.playerState === 1);
        if (info.playerState === 0) h.onEnded?.();
      }
    },
  });

  const cmd = (func, args = []) =>
    core.post({ event: 'command', func, args, id: WIDGET_ID, channel: 'widget' });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') cmd('playVideo');
    else if (verb === 'pause') cmd('pauseVideo');
    else if (verb === 'seek') cmd('seekTo', [ms / 1000, true]);
  });
}

// ---- Vimeo -----------------------------------------------------------------
// Confirmed working. Events must be subscribed to before they arrive.
function vimeoAdapter(frame, h) {
  const core = base(frame, 'https://player.vimeo.com', {
    handshake: (post) => {
      post({ method: 'ping' });
      for (const ev of ['timeupdate', 'play', 'pause', 'error', 'ended']) {
        post({ method: 'addEventListener', value: ev });
      }
    },
    onMessage(data, markReady) {
      markReady();
      h.onReady?.();
      if (data.event === 'timeupdate' && Number.isFinite(data.data?.seconds)) {
        h.onPosition?.(data.data.seconds * 1000);
        if (Number.isFinite(data.data.duration)) h.onDuration?.(data.data.duration * 1000);
        return;
      }
      if (data.event === 'play') return h.onPlaying?.(true);
      if (data.event === 'pause') return h.onPlaying?.(false);
      if (data.event === 'ended') return h.onEnded?.();

      // DRM. Announced on its own, before and independently of any error
      // event, and it is the real reason a protected video shows nothing.
      if (data.event === 'drminitfailed') {
        h.onError?.(0, 'This video is protected by DRM, which does not play in an embed here.');
        return;
      }
      if (data.event === 'error') {
        // An error naming a METHOD is our command being refused, not the video
        // failing. Tearing the player down for one throws away a good video.
        if (data.data?.method) return;
        h.onError?.(0, String(data.data?.message || 'Vimeo could not play this video.').slice(0, 200));
      }
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post({ method: 'play' });
    else if (verb === 'pause') core.post({ method: 'pause' });
    else if (verb === 'seek') core.post({ method: 'setCurrentTime', value: ms / 1000 });
  });
}

// ---- SoundCloud ------------------------------------------------------------
// From the published Widget API, which is postMessage underneath its script.
// Positions are in MILLISECONDS here, unlike most of the others.
function soundcloudAdapter(frame, h) {
  const core = base(frame, 'https://w.soundcloud.com', {
    handshake: (post) => {
      for (const ev of ['ready', 'play', 'pause', 'finish', 'playProgress', 'error']) {
        post({ method: 'addEventListener', value: ev });
      }
      post({ method: 'getDuration' });
    },
    onMessage(data, markReady) {
      markReady();
      h.onReady?.();
      const v = data.value;
      if (data.method === 'playProgress' && Number.isFinite(v?.currentPosition)) {
        h.onPosition?.(v.currentPosition);
        return;
      }
      if (data.method === 'getDuration' && Number.isFinite(v)) return h.onDuration?.(v);
      if (data.method === 'play') return h.onPlaying?.(true);
      if (data.method === 'pause') return h.onPlaying?.(false);
      if (data.method === 'finish') return h.onEnded?.();
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post({ method: 'play' });
    else if (verb === 'pause') core.post({ method: 'pause' });
    else if (verb === 'seek') core.post({ method: 'seekTo', value: ms });
  });
}

// ---- RuTube ----------------------------------------------------------------
// From RuTube's published player messaging: {type, data} both ways.
function rutubeAdapter(frame, h) {
  const core = base(frame, 'https://rutube.ru', {
    handshake: (post) => {
      post({ type: 'player:getCurrentTime', data: {} });
      post({ type: 'player:getState', data: {} });
    },
    onMessage(data, markReady) {
      if (typeof data.type !== 'string' || !data.type.startsWith('player:')) return;
      markReady();
      h.onReady?.();
      const d = data.data || {};
      if (Number.isFinite(d.time)) h.onPosition?.(d.time * 1000);
      if (Number.isFinite(d.duration)) h.onDuration?.(d.duration * 1000);
      if (data.type === 'player:changeState' && typeof d.state === 'string') {
        h.onPlaying?.(d.state === 'playing');
        if (d.state === 'stopped') h.onEnded?.();
      }
      if (data.type === 'player:complete') h.onEnded?.();
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post({ type: 'player:play', data: {} });
    else if (verb === 'pause') core.post({ type: 'player:pause', data: {} });
    else if (verb === 'seek') core.post({ type: 'player:setCurrentTime', data: { time: ms / 1000 } });
  });
}

// ---- OK.ru -----------------------------------------------------------------
// From OK's published player API: {command} out, {event} back.
function okAdapter(frame, h) {
  const core = base(frame, 'https://ok.ru', {
    handshake: (post) => post({ command: 'addEventListener', param: 'ok' }),
    onMessage(data, markReady) {
      if (!data.event && !data.command) return;
      markReady();
      h.onReady?.();
      if (Number.isFinite(data.time)) h.onPosition?.(data.time * 1000);
      if (Number.isFinite(data.duration)) h.onDuration?.(data.duration * 1000);
      if (data.event === 'playing' || data.event === 'play') h.onPlaying?.(true);
      else if (data.event === 'paused' || data.event === 'pause') h.onPlaying?.(false);
      else if (data.event === 'ended' || data.event === 'complete') h.onEnded?.();
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post({ command: 'play' });
    else if (verb === 'pause') core.post({ command: 'pause' });
    else if (verb === 'seek') core.post({ command: 'seek', seek: ms / 1000 });
  });
}

// ---- Niconico --------------------------------------------------------------
// From Niconico's published embed messaging. Everything is in milliseconds,
// and every message must carry sourceConnectorType and playerId.
function niconicoAdapter(frame, h) {
  const envelope = (eventName, data) => ({ eventName, data, sourceConnectorType: 1, playerId: '1' });

  const core = base(frame, 'https://embed.nicovideo.jp', {
    handshake: (post) => {
      post(envelope('registerCallback', {}));
      // Ask outright, so the room can be driven before playback rather than
      // only once something has already started.
      post(envelope('getStatus', {}));
    },
    onMessage(data, markReady) {
      if (typeof data.eventName !== 'string') return;
      markReady();
      h.onReady?.();
      const d = data.data || {};
      if (Number.isFinite(d.currentTime)) h.onPosition?.(d.currentTime);
      if (Number.isFinite(d.duration)) h.onDuration?.(d.duration);
      // loadComplete carries the video's details; the length lives under
      // videoInfo and is the first hard number available.
      const vi = d.videoInfo || {};
      const len = Number(vi.lengthInSeconds ?? vi.duration ?? vi.length);
      if (Number.isFinite(len) && len > 0) h.onDuration?.(len * 1000);
      if (data.eventName === 'playerStatusChange') {
        // 2 is playing; 4 is ended.
        if (d.playerStatus === 2) h.onPlaying?.(true);
        else if (d.playerStatus === 3 || d.playerStatus === 1) h.onPlaying?.(false);
        else if (d.playerStatus === 4) h.onEnded?.();
      }
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post(envelope('play', {}));
    else if (verb === 'pause') core.post(envelope('pause', {}));
    else if (verb === 'seek') core.post(envelope('seek', { time: ms }));
  });
}

// ---- Dailymotion -----------------------------------------------------------
// The player announces itself with events; commands go back as {command}.
function dailymotionAdapter(frame, h) {
  const core = base(frame, 'https://geo.dailymotion.com', {
    handshake: (post) => post({ command: 'notifyOnEvents', parameters: [] }),
    onMessage(data, markReady) {
      const ev = data.event || data.eventName;
      if (typeof ev !== 'string') return;
      markReady();
      h.onReady?.();
      const t = Number(data.time ?? data.currentTime);
      if (Number.isFinite(t)) h.onPosition?.(t * 1000);
      const dur = Number(data.duration);
      if (Number.isFinite(dur) && dur > 0) h.onDuration?.(dur * 1000);
      if (ev === 'play' || ev === 'playing') h.onPlaying?.(true);
      else if (ev === 'pause') h.onPlaying?.(false);
      else if (ev === 'video_end' || ev === 'ended') h.onEnded?.();
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post({ command: 'play', parameters: [] });
    else if (verb === 'pause') core.post({ command: 'pause', parameters: [] });
    else if (verb === 'seek') core.post({ command: 'seek', parameters: [ms / 1000] });
  });
}

// ---- VK Video --------------------------------------------------------------
// From VK's published player API: {method, value} out, {type:'player', event}
// back.
function vkAdapter(frame, h) {
  const core = base(frame, 'https://vkvideo.ru', {
    handshake: (post) => post({ method: 'addEventListener', value: ['started', 'paused', 'ended', 'timeupdate'] }),
    onMessage(data, markReady) {
      if (data.type !== 'player' && !data.event) return;
      markReady();
      h.onReady?.();
      const t = Number(data.time ?? data.value?.time);
      if (Number.isFinite(t)) h.onPosition?.(t * 1000);
      const dur = Number(data.duration ?? data.value?.duration);
      if (Number.isFinite(dur) && dur > 0) h.onDuration?.(dur * 1000);
      if (data.event === 'started' || data.event === 'resumed') h.onPlaying?.(true);
      else if (data.event === 'paused') h.onPlaying?.(false);
      else if (data.event === 'ended') h.onEnded?.();
    },
  });

  return adapter(core, (verb, ms) => {
    if (verb === 'play') core.post({ method: 'play', value: {} });
    else if (verb === 'pause') core.post({ method: 'pause', value: {} });
    else if (verb === 'seek') core.post({ method: 'seek', value: ms / 1000 });
  });
}

/** Everything with no adapter. Watched normally, position not shared. */
function inertAdapter() {
  return { attemptsSync: false, send() {}, destroy() {} };
}

const ADAPTERS = {
  youtube: youtubeAdapter,
  vimeo: vimeoAdapter,
  soundcloud: soundcloudAdapter,
  rutube: rutubeAdapter,
  ok: okAdapter,
  niconico: niconicoAdapter,
  dailymotion: dailymotionAdapter,
  vk: vkAdapter,
};

/**
 * @param {string|null} control the provider's `control` field, or null
 * @param {HTMLIFrameElement} frame
 * @param {object} handlers
 */
export function createVideoAdapter(control, frame, handlers = {}) {
  const make = control && ADAPTERS[control];
  return make ? make(frame, handlers) : inertAdapter();
}

export default { createVideoAdapter };
